import { HttpClient, HttpHeaders, HttpEvent, HttpEventType } from "@angular/common/http";
import { Observable, TimeoutError, of, throwError } from "rxjs";
import { catchError, filter, map, timeout } from 'rxjs/operators';
// import { Intercom } from 'ng-intercom';
import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { LoaderService } from "./loader/loader.service";
import { environment } from "../../../environments/environment";
import { localStorageService } from "./localstorage.service";
import { ToasterService } from "./toaster/toaster.service";
import { ExportEvent } from "./export-events";
import { SomethingWentWrongService } from "./something-went-wrong-modal/something-went-wrong.service";

@Injectable()
export class ApiService {
  headers: any = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept': '*/*' });
  headersObject: any = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept': '*/*' });
  timeoutError: boolean = false;
  deviceInfo = {
    device_info: this.device_info()
  };
  constructor(private _toaster: ToasterService, public http: HttpClient, private _local: localStorageService,
    private router: Router, private _loader: LoaderService, private _somethingWentWrong: SomethingWentWrongService) { }
  /**
   * @param options.silent Skips the full-screen loader for this one call, for
   * callers that show their own in-place state (skeletons, a busy button).
   * The loader is a single shared flag, so a silent call must not hide it
   * either — a concurrent normal request may still be running behind it.
   */
  api(_data: any, apiName: any, method: string = 'post', options: { silent?: boolean } = {}): Observable<any> {
     /**
     * Screens that render their own inline loading UI (skeletons/shimmers) can pass
     * hide_loader: true to suppress the global overlay loader. It's stripped here so it
     * never reaches the API, and hideLoader() is skipped too — otherwise this request
     * would dismiss a loader a concurrent request is legitimately showing.
     */
    const hideGlobalLoader = !!_data['hide_loader'];
    delete _data['hide_loader'];
    /**
     * Either signal on its own suppresses the loader, so they must be OR'd here
     * (AND'd in the guards below) — testing them separately let whichever flag
     * the caller did not pass turn the loader back on.
     */
    const skipGlobalLoader = !!options.silent || hideGlobalLoader;
    _data['country_id'] = this._local.customGetItem('country_id');
    _data['cocu_id'] = this._local.customGetItem('cocu_id');
    _data['show_toaster_messages'] = _data['show_toaster_messages'] ? _data['show_toaster_messages'] : false;
    let apiData: any = {};

    Object.keys(_data).filter(key => {
      if (_data[key] || _data[key] == 0) {
        apiData[key] = _data[key];
      }
    });
    if (method != 'get') {
      apiData = { ...apiData, ...this.deviceInfo }
    }

    let hitting_url = environment.baseUrl + apiName;
    if (_data.is_export && _data.is_export == 1) {
      hitting_url = environment.exportUrl + apiName;
    }
    
    let output;
    if (method === 'get') {
      output = this.http.get<any>(hitting_url, { headers: this.headers, params: apiData });
    } else if (method === 'put') {
      output = this.http.put<any>(hitting_url, apiData, { headers: this.headers });
    } else if (method === 'object') {
      output = this.http.post<any>(hitting_url, apiData, { headers: this.headersObject });
    } else {
      output = this.http.post<any>(hitting_url, apiData, { headers: this.headers });
    }
    if (!_data['show_toaster_messages']){
      this.hideToastr();
    }
    if (!skipGlobalLoader) {
      this._loader.showLoader();
    }
    return output
      .pipe(
        timeout(60000),
        // No retry: re-sending a request whose first attempt may still be processing
        // server side (e.g. a charge) can apply the same transaction twice.
        map((data: any) => {
          if (!skipGlobalLoader) {
            this._loader.hideLoader();
          }
          if ((data.flag === 500 || data.flag === 401) && apiName != 'finance/incomming_call_details') {
            this.customPopups(data.original_text, 1);
            this._local.customClearAllItems();
            this.loginRedirect();
            return false;
          }
          if (data.flag === 504) {
            this.customPopups(data.original_text, 1);
            setTimeout(()=>{
              this.loginRedirect();
            },2000)
            
            this._local.customClearAllItems();
            
            return false;
          }
          if (data && data.original_text && apiName != 'finance/incomming_call_details') {
            this.customPopups(data.original_text || data.err, 1);
            // return false;
          }
          return data;
        }),
        catchError((error: any) => {
          if (!skipGlobalLoader) {
            this._loader.hideLoader();
          }
          if (error instanceof TimeoutError) {
            if (!this.timeoutError) {
              this.timeoutError = true;
            }
            // A timeout cancels the request before it fails, so the interceptor never sees it.
            this._somethingWentWrong.show();
            return throwError(() => { error: 'Timeout Exception' });
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Single-shot GET for the heavy all-product exports. Deliberately separate from api():
   * it must not inherit api()'s retry(3), which turns one timed-out export into four runs
   * of the same expensive query against the export server. Longer timeout instead, since
   * these legitimately take a while. Nothing else calls this, so no other flow is affected.
   *
   * Emits download progress as it goes so the UI can show a real bar, then one final
   * 'response' event carrying the body.
   */
  exportApi(_data: any, apiName: string): Observable<ExportEvent> {
    const source: any = { ..._data, country_id: this._local.customGetItem('country_id') };
    const apiData: any = {};
    Object.keys(source).forEach(key => {
      if (source[key] || source[key] == 0) {
        apiData[key] = source[key];
      }
    });

    return this.http.get(environment.exportUrl + apiName, {
      headers: this.headers,
      params: apiData,
      observe: 'events',
      reportProgress: true
    })
      .pipe(
        // Two separate budgets. The export server can legitimately take minutes to produce
        // the first byte, but once it is streaming, a long gap means the transfer stalled.
        // A single overall timeout would have to be as generous as the slowest whole
        // download, which would let a dead connection hang for just as long.
        timeout({ first: 300000, each: 120000 }),
        map((event: HttpEvent<any>): ExportEvent | null => {
          if (event.type === HttpEventType.DownloadProgress) {
            // total is absent when the response is chunked or gzipped without a
            // Content-Length; 0 tells the bar to stay indeterminate rather than fake a %.
            return { kind: 'progress', progress: { loaded: event.loaded, total: event.total || 0 } };
          }
          if (event.type === HttpEventType.Response) {
            const body: any = event.body;
            if (body && (body.flag === 500 || body.flag === 401)) {
              this.customPopups(body.original_text, 1);
              this._local.customClearAllItems();
              this.loginRedirect();
              throw new Error('Session expired');
            }
            return { kind: 'response', body };
          }
          return null;
        }),
        filter((event: ExportEvent | null): event is ExportEvent => event !== null),
        catchError((error: any) => {
          if (error instanceof TimeoutError) {
            return throwError(() => ({ error: 'Timeout Exception' }));
          }
          return throwError(() => error);
        })
      );
  }

  uploadApi(_data: any, apiName: any, options: any = {}): Observable<any> {
    let api_url = environment.baseUrl + apiName;
    if (!api_url) {
      api_url = environment.baseUrl;
    }
    this.hideToastr();
    this._loader.showLoader();
    return this.http.post<any>(`${api_url}`, _data, options)
      .pipe(
        timeout(60000),
        map((data: any) => {
          this._loader.hideLoader();
          if (data.flag === 377) {
            this.loginRedirect();
            return false;
          }
          if (data.flag === 4) {
            this.loginRedirect();
            if (data && data.original_text) {
              this.customPopups(data.original_text, 1);
            }
            return false;
          }
          if (data) {
            if (data.err || data.original_text) {
              if (data.original_text) {
                this.customPopups(data.original_text, 1);
              } else {
                this.customPopups(data.err, 1);
              }
            } else {
              // this.customPopups(data.flag, data.original_text);
            }
          }
          return data;
        }),
        catchError((error: any) => {
          this._loader.hideLoader();
          if (error instanceof TimeoutError) {
            if (!this.timeoutError) {
              this.timeoutError = true;
              this.customPopups('Request Timeout', 1);
            }
            return throwError({ error: 'Timeout Exception' });
          }
          if (error.status === 0) {
            if (!this.timeoutError) {
              this.customPopups('Unable to fetch data from server', 1);
            }
          }
          return throwError(error);
        })
      )
  }

  deleteServerFile(file: any, api_name: string) {
    let params = {
      url: file
    }
    return this.http.post(environment.baseUrl + api_name, params)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error: any) => {
          return error;
        })
      )
  }

  loginRedirect() {
    this._local.customClearAllItems();
    // this.intercom.shutdown();
    this.router.navigate(['login']);
    window.location.reload();
  }

  device_info() {
    var window: any = window;
    var document: any = document;
    // Opera 8.0+
    const isOpera = navigator.userAgent.indexOf(' OPR/') >= 0 || navigator.userAgent.indexOf("Opera") >= 0;
    // Firefox 1.0+
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    // Safari 3.0+ "[object HTMLElementConstructor]"
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    // Edge 20+
    const isEdge = navigator.userAgent.indexOf("Edge") > -1;
    // Internet Explorer 6-11
    const isIE = typeof document !== 'undefined' && !!document.documentMode && !isEdge;
    // Chrome 1 - 71
    const isChrome = !isOpera && !isEdge && !isSafari;
    // Blink engine detection
    const isBlink = (isChrome || isOpera) && (window && !!window.CSS);
    const browserInfo = {
      isFirefox,
      isChrome,
      isSafari,
      isOpera,
      isIE,
      isEdge,
      isBlink
    };
    const platform = navigator && navigator.platform ? navigator.platform : 'unknown';
    const userAgent = navigator && navigator.userAgent ? navigator.userAgent : 'unknown';
    return {
      browser: browserInfo,
      platform,
      userAgent
    };
  }

  downloadApi(data: any, url: any, view: any) {
    let config = {
      // headers: this.headers,
      responseType: 'blob' as 'json',
      observe: 'response' as 'body',
      params: data
    };
    let api_url = environment.exportUrl;
    this._loader.showLoader();
    return this.http.get<any>(`${api_url}${url}`, config).pipe(
      timeout(60000),
      map((data: any) => {
        this._loader.hideLoader();
        if (!data.is_error && data.headers && data.headers.get('Content-Disposition')) {
          let headers = data.headers.get('Content-Disposition');
          if (!headers) {
            throw new Error('Something went wrong, please try again later');
          }
          let fileName = this.extractFirstText(headers);
          data = data.body;
          let blob = new Blob([data], { type: 'application/pdf' });
          let urlwindow = window.URL.createObjectURL(blob);
          if (view == 0) {
            let anchor = document.createElement("a");
            anchor.download = fileName;
            anchor.href = urlwindow;
            anchor.click();
          } else if (view == 1) {
            let pwa = window.open(urlwindow);
            if (!pwa || pwa.closed || typeof pwa.closed == 'undefined') {
              alert("Please disable your browser's pop-up blocker and try again.");
            }
          }
          else if (view == 2) {
            let w: any = window.open(urlwindow, "", "width=800,height=800");
            if (!w || w.closed || typeof w.closed == 'undefined') {
              alert("Please disable your browser's pop-up blocker and try again.");
            }
            if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
              w.location.reload();
            }
            w.focus();
            w.print();
          }
        } else {
          if (data.flag == 400) {
            this.customPopups(data.original_text, 1);
            return
          }
        }
      }),
      catchError((error: any) => {
        this._loader.hideLoader();
        if (error instanceof TimeoutError) {
          if (!this.timeoutError) {
            this.timeoutError = true;
            this.customPopups('Request Timeout', 1);
          }
          throw new Error('Request Timeout');
        }
        if (error.status == 0) {
          if (!this.timeoutError) {
            this.customPopups('Unable to fetch data from server', 1);
          }
        }
        this.customPopups(error, 1);
        throw new Error(error);
      })
    );
  }

  customPopups(message: string, flag: Number) {
    if (flag == 1) {
      this._toaster.showToaster('Error', message, 'error');
    } else if (flag == 2) {
      this._toaster.showToaster('Warning', message, 'warning');
    } else {
      this._toaster.showToaster('Success', message, 'success');
    }
  }

  hideToastr() {
    this._toaster.hideAllToasters();
  }

  extractFirstText(str: any) {
    const matches = str && str.match(/"(.*?)"/);
    return matches ? matches[1] : str;
  }
  gt_get_cookie(cname:any) {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  uploadFileProgressApi(
    _data: any,
    url: any,
    flag: any,
    method?: string
  ): Observable<any> {
    let api_url = localStorage.getItem("baseUrl");
    if (!api_url || api_url == "undefined" || api_url == undefined) {
      localStorage.removeItem("baseUrl");
      api_url = environment.baseUrl;
    }
    return this.http
      .post<any>(`${api_url}${url}`, _data, {
        reportProgress: true,
        observe: "events",
      })
      .pipe(
        timeout(60000*60),
        map((data: any) => {
          if (data.type === HttpEventType.UploadProgress) {
            const progress = Math.round((100 * data.loaded) / data.total);
            console.log(`File is ${progress}% uploaded.`);
            return { upload_progress: progress };
          } else if (data.type === HttpEventType.Response) {
            console.log(`File was completely uploaded!`);
          }
          if (data.type == 4) {
            data = data.body;
            if (data.flag === 377) {
              this.loginRedirect();
              return false;
            }
            if (data.flag === 4 || data.flag === 309) {
              this.loginRedirect();
              if (data && (data.override_text || data.err)) {
                this.customPopups(data.override_text || data.err, 1);
              }
              return false;
            }
            if (data && flag) {
              if (data.err) {
                if (data.override_text) {
                  this.customPopups(data.override_text, 1);
                } else {
                  this.customPopups(data.err, 1);
                }
              } else {
                // this.flagPopUps(data.flag, data.is_error);
              }
            }

            console.log(data);

            return data;
          }
          return {};
        }),
        catchError((error: any) => {
          if (error instanceof TimeoutError) {
            if (!this.timeoutError) {
              this.timeoutError = true;
              this.customPopups("Request Timeout", 1);
            }
            return throwError({ error: "Timeout Exception" });
          }
          if (error.status === 0) {
            if (!this.timeoutError) {
              this.customPopups("Unable to fetch data from server", 1);
            }
          }
          return throwError(error);
        })
      );
  }
}


